import { addDays, percentageOf } from "./core";
import type {
  AccountType, AccountingSettings, BusinessDate, ComplimentaryApproval, EmployeeAllowance, Expense, InventoryItem, JournalEntry, LedgerAccount, Order, Payment, PaymentMethod,
  PayrollLine, PayrollRun, PayrollTotals, Purchase, Refund, Shift, StockMovement, SupplierBill, SupplierPayment, TaxSlab
} from "./types";

/**
 * The books of the accounts app. Two kinds of entries make up the ledger:
 *  - the POS figures, worked out here from the POS records each time (`posEntries`), one entry per day and kind, so the
 *    books can never disagree with the POS and nothing is copied into the database;
 *  - the journals the accountant posts (opening balances, payroll, adjustments), stored as `journalEntries`.
 * Money is integer cents throughout, like the rest of the system.
 */

/** The accounts the POS figures and payroll are posted to. Each default account below carries one of these keys. */
export const SYSTEM_KEYS = [
  "cash", "card", "bank", "receivable", "sales", "discounts", "sales_returns", "service_charge", "vat_output", "vat_input",
  "inventory", "cogs", "stock_loss", "supplier_payable", "purchase_paid_from", "cash_over_short", "other_expenses",
  "salaries", "epf_employer", "etf_expense", "epf_payable", "etf_payable", "apit_payable", "salaries_payable", "salary_advances",
  "staff_deductions", "opening_equity", "retained_earnings"
] as const;
export type SystemKey = (typeof SYSTEM_KEYS)[number];
export const SYSTEM_KEY_LABELS: Record<SystemKey, string> = {
  cash: "Cash takings (drawer)", card: "Card payments", bank: "Bank transfers", receivable: "Customer bills not yet paid", sales: "Food & beverage sales",
  discounts: "Discounts given", sales_returns: "Refunds to customers", service_charge: "Service charge collected", vat_output: "VAT collected (output)",
  vat_input: "VAT paid on purchases (input)", inventory: "Stock (inventory)", cogs: "Cost of food sold", stock_loss: "Stock waste & count differences",
  supplier_payable: "Owed to suppliers", purchase_paid_from: "Purchases paid at once (not on credit)", cash_over_short: "Drawer over / short",
  other_expenses: "Expenses with no category account", salaries: "Salaries & wages", epf_employer: "EPF employer 12%", etf_expense: "ETF 3%",
  epf_payable: "EPF payable", etf_payable: "ETF payable", apit_payable: "APIT payable", salaries_payable: "Net salaries payable",
  salary_advances: "Salary advances", staff_deductions: "Other deductions from salaries", opening_equity: "Opening balances", retained_earnings: "Retained earnings"
};

type DefaultAccount = { code: string; name: string; type: AccountType; key?: SystemKey };
/** A starting chart of accounts for a Sri Lankan restaurant. The accountant can rename, add and deactivate accounts. */
export const DEFAULT_ACCOUNTS: readonly DefaultAccount[] = [
  { code: "1000", name: "Cash on hand", type: "asset", key: "cash" },
  { code: "1010", name: "Petty cash", type: "asset" },
  { code: "1100", name: "Bank - current account", type: "asset", key: "bank" },
  { code: "1110", name: "Card settlements receivable", type: "asset", key: "card" },
  { code: "1200", name: "Customer bills receivable", type: "asset", key: "receivable" },
  { code: "1300", name: "Inventory - food & beverage", type: "asset", key: "inventory" },
  { code: "1400", name: "VAT input receivable", type: "asset", key: "vat_input" },
  { code: "1410", name: "Salary advances", type: "asset", key: "salary_advances" },
  { code: "1420", name: "Prepayments & deposits", type: "asset" },
  { code: "1500", name: "Furniture & equipment", type: "asset" },
  { code: "1510", name: "Kitchen equipment", type: "asset" },
  { code: "1590", name: "Accumulated depreciation", type: "asset" },
  { code: "2000", name: "Suppliers payable", type: "liability", key: "supplier_payable" },
  { code: "2100", name: "VAT output payable", type: "liability", key: "vat_output" },
  { code: "2110", name: "SSCL payable", type: "liability" },
  { code: "2200", name: "Service charge payable to staff", type: "liability", key: "service_charge" },
  { code: "2300", name: "Salaries payable", type: "liability", key: "salaries_payable" },
  { code: "2310", name: "EPF payable", type: "liability", key: "epf_payable" },
  { code: "2320", name: "ETF payable", type: "liability", key: "etf_payable" },
  { code: "2330", name: "APIT payable", type: "liability", key: "apit_payable" },
  { code: "2400", name: "Accrued expenses", type: "liability" },
  { code: "2500", name: "Loans", type: "liability" },
  { code: "3000", name: "Owner's capital", type: "equity", key: "opening_equity" },
  { code: "3100", name: "Owner's drawings", type: "equity" },
  { code: "3200", name: "Retained earnings", type: "equity", key: "retained_earnings" },
  { code: "4000", name: "Food & beverage sales", type: "income", key: "sales" },
  { code: "4010", name: "Discounts given", type: "income", key: "discounts" },
  { code: "4020", name: "Sales returns & refunds", type: "income", key: "sales_returns" },
  { code: "4100", name: "Other income", type: "income" },
  { code: "4110", name: "Staff deductions recovered", type: "income", key: "staff_deductions" },
  { code: "4120", name: "Cash over / short", type: "income", key: "cash_over_short" },
  { code: "5000", name: "Cost of food & beverage sold", type: "expense", key: "cogs" },
  { code: "5010", name: "Stock waste & adjustments", type: "expense", key: "stock_loss" },
  { code: "6000", name: "Salaries & wages", type: "expense", key: "salaries" },
  { code: "6010", name: "EPF - employer contribution", type: "expense", key: "epf_employer" },
  { code: "6020", name: "ETF contribution", type: "expense", key: "etf_expense" },
  { code: "6100", name: "Rent", type: "expense" },
  { code: "6110", name: "Electricity", type: "expense" },
  { code: "6120", name: "Water", type: "expense" },
  { code: "6130", name: "Gas", type: "expense" },
  { code: "6140", name: "Transport", type: "expense" },
  { code: "6150", name: "Repairs & maintenance", type: "expense" },
  { code: "6160", name: "Packaging", type: "expense" },
  { code: "6170", name: "Cleaning", type: "expense" },
  { code: "6180", name: "Marketing", type: "expense" },
  { code: "6190", name: "Bank charges & card fees", type: "expense" },
  { code: "6200", name: "Depreciation", type: "expense" },
  { code: "6210", name: "SSCL expense", type: "expense" },
  { code: "6900", name: "Other expenses", type: "expense", key: "other_expenses" }
];
/** The POS expense categories (Expenses screen) and the default account each is posted to, by account code. */
/** The POS expense category for a salary advance paid to a person; it goes to the Salary advances asset, not to costs. */
export const SALARY_ADVANCE = "Salary advance";
export const DEFAULT_EXPENSE_CODES: Record<string, string> = {
  Ingredients: "5000", Gas: "6130", Electricity: "6110", Water: "6120", Rent: "6100", Salaries: "6000", Transport: "6140",
  Maintenance: "6150", Packaging: "6160", Cleaning: "6170", Marketing: "6180", Other: "6900"
};

/**
 * Monthly APIT on employment income from 1 April 2025: LKR 150,000 a month free, then 6%, 18%, 24%, 30% bands and 36% on
 * the rest. Kept in the accounting settings so the accountant can change it when the Inland Revenue does.
 */
export const DEFAULT_APIT_RELIEF_MINOR = 15_000_000;
export const DEFAULT_APIT_SLABS: TaxSlab[] = [
  { widthMinor: 8_333_333, bps: 600 }, { widthMinor: 4_166_667, bps: 1800 }, { widthMinor: 4_166_667, bps: 2400 },
  { widthMinor: 4_166_667, bps: 3000 }, { widthMinor: null, bps: 3600 }
];

export function defaultAccountingSettings(booksStartDate: BusinessDate, accounts: readonly LedgerAccount[], iso: string): AccountingSettings {
  const byCode = new Map(accounts.map((account) => [account.code, account.id]));
  const expenseAccounts = Object.fromEntries(Object.entries(DEFAULT_EXPENSE_CODES).flatMap(([category, code]) => byCode.has(code) ? [[category, byCode.get(code)!]] : []));
  return {
    id: "accounting", booksStartDate, lockedThrough: null, mapping: {}, expenseAccounts, costing: "periodic",
    vatBps: 1800, ssclBps: 250, ssclLiableBps: 10_000, epfEmployeeBps: 800, epfEmployerBps: 1200, etfBps: 300,
    apitReliefMinor: DEFAULT_APIT_RELIEF_MINOR, apitSlabs: DEFAULT_APIT_SLABS,
    employerName: "", tin: "", vatNo: "", epfRegNo: "", etfRegNo: "", updatedAt: iso, schemaVersion: 1
  };
}

// ---------------------------------------------------------------- ledger entries

export type LedgerLine = { accountId: string; debitMinor: number; creditMinor: number; memo: string };
/** One entry of the ledger, whether worked out from the POS (`pos:` ids) or posted by the accountant. */
export type LedgerEntry = {
  id: string; number: string; businessDate: BusinessDate; memo: string; reference: string; lines: LedgerLine[];
  origin: "pos" | "journal"; kind: string; reversed?: boolean;
};

export type PosRecords = {
  orders: readonly Order[]; payments: readonly Payment[]; refunds: readonly Refund[]; expenses: readonly Expense[];
  purchases: readonly Purchase[]; supplierPayments: readonly SupplierPayment[]; shifts: readonly Shift[];
  /** Only waste, adjustment and count movements matter here (sales are costed from the bills). */
  stockMovements: readonly StockMovement[];
  complimentary?: readonly ComplimentaryApproval[];
  /** Supplier bills entered on the POS or by the accountant (engine/backoffice.ts). */
  supplierBills?: readonly SupplierBill[];
};

/** Resolves each system key and expense category to an account id: the accountant's mapping, else the account carrying the key. */
export class AccountMap {
  private readonly byKey = new Map<string, string>();
  private readonly byId = new Map<string, LedgerAccount>();
  constructor(readonly accounts: readonly LedgerAccount[], private readonly settings: Pick<AccountingSettings, "mapping" | "expenseAccounts">) {
    for (const account of accounts) {
      this.byId.set(account.id, account);
      if (account.key) this.byKey.set(account.key, account.id);
    }
  }
  key(key: SystemKey): string {
    const mapped = this.settings.mapping[key];
    if (mapped && this.byId.has(mapped)) return mapped;
    return this.byKey.get(key) ?? `missing:${key}`;
  }
  method(method: PaymentMethod): string { return this.key(method === "cash" ? "cash" : method === "card" ? "card" : "bank"); }
  expense(category: string): string {
    const mapped = this.settings.expenseAccounts[category];
    if (mapped && this.byId.has(mapped)) return mapped;
    return category === SALARY_ADVANCE ? this.key("salary_advances") : this.key("other_expenses");
  }
  account(id: string): LedgerAccount | undefined { return this.byId.get(id); }
}

/** Builds lines, adding together everything posted to the same account on the same side, and dropping zero lines. */
class Lines {
  private readonly map = new Map<string, LedgerLine>();
  debit(accountId: string, amount: number, memo = "") { this.add(accountId, amount, 0, memo); return this; }
  credit(accountId: string, amount: number, memo = "") { this.add(accountId, 0, amount, memo); return this; }
  private add(accountId: string, debit: number, credit: number, memo: string) {
    if (debit < 0) { credit = -debit; debit = 0; }
    if (credit < 0) { debit = -credit; credit = 0; }
    if (debit === 0 && credit === 0) return;
    const id = `${accountId}|${debit > 0 ? "d" : "c"}|${memo}`;
    const line = this.map.get(id) ?? { accountId, debitMinor: 0, creditMinor: 0, memo };
    line.debitMinor += debit; line.creditMinor += credit;
    this.map.set(id, line);
  }
  list(): LedgerLine[] { return [...this.map.values()]; }
}

const METHOD_LABEL: Record<PaymentMethod, string> = { cash: "cash", card: "card", bank_transfer: "bank transfer" };
const groupBy = <T>(items: readonly T[], key: (item: T) => string) => {
  const map = new Map<string, T[]>();
  for (const item of items) { const group = map.get(key(item)); if (group) group.push(item); else map.set(key(item), [item]); }
  return map;
};

/** How much of a refund was tax and service charge, in the same proportion as on the bill it refunds. */
export function splitRefund(refund: Pick<Refund, "amountMinor">, order: Pick<Order, "totals"> | undefined) {
  const total = order?.totals.totalMinor ?? 0;
  if (!order || total <= 0) return { taxMinor: 0, serviceMinor: 0, netMinor: refund.amountMinor };
  const taxMinor = Math.round(refund.amountMinor * order.totals.taxMinor / total);
  const serviceMinor = Math.round(refund.amountMinor * order.totals.serviceChargeMinor / total);
  return { taxMinor, serviceMinor, netMinor: refund.amountMinor - taxMinor - serviceMinor };
}

/** The value of a stock movement at the cost it was recorded with (negative when stock went out). */
export const movementValue = (movement: Pick<StockMovement, "deltaMilli" | "unitCostMinor">) => Math.round(movement.deltaMilli * movement.unitCostMinor / 1000);
export const STOCK_ADJUSTMENT_KINDS = ["waste", "adjustment", "count"] as const;

/**
 * The POS records as ledger entries, one per business day and kind. Records dated before `from` are left out (the opening
 * balances cover them). Sales are counted on the day the bill was created once it is paid or completed (`isSale`), and money on the day it was
 * received; the "customer bills receivable" account carries the difference between the two (bills still open).
 * With periodic costing, purchases are cost of sales at once and bills carry no cost; with perpetual costing, purchases go to
 * stock, each bill's recipe cost leaves it, and waste and count differences are posted from the stock movements.
 */
export function posEntries(records: PosRecords, map: AccountMap, from: BusinessDate, to: BusinessDate, costing: AccountingSettings["costing"] = "periodic"): LedgerEntry[] {
  const perpetual = costing === "perpetual";
  const inRange = (date: string) => date >= from && date <= to;
  const entries: LedgerEntry[] = [];
  const push = (businessDate: string, kind: string, memo: string, lines: Lines, reference = "") => {
    const list = lines.list();
    if (list.length > 0) entries.push({ id: `pos:${kind}:${businessDate}`, number: `POS-${businessDate.replaceAll("-", "")}-${kind.toUpperCase().slice(0, 3)}`, businessDate, memo, reference, lines: list, origin: "pos", kind });
  };
  const ordersById = new Map(records.orders.map((order) => [order.id, order]));

  for (const [date, orders] of groupBy(records.orders.filter((order) => isSale(order) && inRange(order.businessDate)), (order) => order.businessDate)) {
    const lines = new Lines();
    for (const order of orders) {
      const t = order.totals;
      lines.debit(map.key("receivable"), t.totalMinor)
        .debit(map.key("discounts"), t.discountMinor)
        .credit(map.key("sales"), t.grossMinor - t.complimentaryMinor)
        .credit(map.key("service_charge"), t.serviceChargeMinor)
        .credit(map.key("vat_output"), t.taxMinor);
      if (perpetual) lines.debit(map.key("cogs"), t.costMinor).credit(map.key("inventory"), t.costMinor);
    }
    push(date, "sales", `POS sales - ${orders.length} bill${orders.length === 1 ? "" : "s"}`, lines);
  }
  for (const [date, payments] of groupBy(records.payments.filter((payment) => inRange(payment.businessDate)), (payment) => payment.businessDate)) {
    const lines = new Lines();
    for (const payment of payments) lines.debit(map.method(payment.method), payment.amountMinor, `Received by ${METHOD_LABEL[payment.method]}`).credit(map.key("receivable"), payment.amountMinor);
    push(date, "receipts", "Money received for bills", lines);
  }
  for (const [date, refunds] of groupBy(records.refunds.filter((refund) => inRange(refund.businessDate)), (refund) => refund.businessDate)) {
    const lines = new Lines();
    for (const refund of refunds) {
      const split = splitRefund(refund, ordersById.get(refund.orderId));
      lines.debit(map.key("sales_returns"), split.netMinor).debit(map.key("vat_output"), split.taxMinor).debit(map.key("service_charge"), split.serviceMinor)
        .credit(map.method(refund.method), refund.amountMinor, `Refunded by ${METHOD_LABEL[refund.method]}`);
    }
    push(date, "refunds", `Refunds - ${refunds.length}`, lines);
  }
  for (const [date, expenses] of groupBy(records.expenses.filter((expense) => !expense.voided && inRange(expense.businessDate)), (expense) => expense.businessDate)) {
    const lines = new Lines();
    for (const expense of expenses) lines.debit(map.expense(expense.category), expense.amountMinor, expense.category).credit(map.method(expense.method), expense.amountMinor, `Paid by ${METHOD_LABEL[expense.method]}`);
    push(date, "expenses", `POS expenses - ${expenses.length}`, lines);
  }
  for (const [date, purchases] of groupBy(records.purchases.filter((purchase) => inRange(purchase.businessDate)), (purchase) => purchase.businessDate)) {
    const lines = new Lines();
    for (const purchase of purchases) {
      const invoiceMinor = purchase.totalMinor + (purchase.vatMinor ?? 0);
      lines.debit(map.key(perpetual ? "inventory" : "cogs"), purchase.totalMinor).debit(map.key("vat_input"), purchase.vatMinor ?? 0, "Input VAT");
      if (purchase.onCredit) lines.credit(map.key("supplier_payable"), invoiceMinor, "On credit"); else lines.credit(map.key("purchase_paid_from"), invoiceMinor, "Paid at once");
    }
    push(date, "purchases", `Stock purchases - ${purchases.length}`, lines);
  }
  for (const [date, bills] of groupBy((records.supplierBills ?? []).filter((bill) => !bill.voided && inRange(bill.businessDate)), (bill) => bill.businessDate)) {
    const lines = new Lines();
    for (const bill of bills) {
      const cost = bill.amountMinor - bill.vatMinor;
      lines.debit(bill.kind === "stock" ? map.key(perpetual ? "inventory" : "cogs") : map.expense(bill.category), cost, bill.kind === "stock" ? "Stock" : bill.category)
        .debit(map.key("vat_input"), bill.vatMinor)
        .credit(map.key("supplier_payable"), bill.amountMinor, bill.supplierName);
    }
    push(date, "bills", `Supplier bills - ${bills.length}`, lines);
  }
  for (const [date, payments] of groupBy(records.supplierPayments.filter((payment) => inRange(payment.businessDate)), (payment) => payment.businessDate)) {
    const lines = new Lines();
    for (const payment of payments) lines.debit(map.key("supplier_payable"), payment.amountMinor).credit(map.method(payment.method), payment.amountMinor, `Paid by ${METHOD_LABEL[payment.method]}`);
    push(date, "supplier", `Supplier payments - ${payments.length}`, lines);
  }
  for (const [date, shifts] of groupBy(records.shifts.filter((shift) => shift.status === "closed" && typeof shift.varianceMinor === "number" && shift.varianceMinor !== 0 && inRange(shift.businessDate)), (shift) => shift.businessDate)) {
    const lines = new Lines();
    for (const shift of shifts) lines.debit(map.key("cash"), shift.varianceMinor!).credit(map.key("cash_over_short"), shift.varianceMinor!);
    push(date, "drawer", "Cash drawer over / short at shift close", lines);
  }
  const adjustments = !perpetual ? [] : records.stockMovements.filter((movement) => (STOCK_ADJUSTMENT_KINDS as readonly string[]).includes(movement.kind) && movement.refType !== "opening" && inRange(movement.businessDate));
  for (const [date, movements] of groupBy(adjustments, (movement) => movement.businessDate)) {
    const lines = new Lines();
    for (const movement of movements) { const value = movementValue(movement); lines.debit(map.key("inventory"), value).credit(map.key("stock_loss"), value); }
    push(date, "stock", "Stock waste, adjustments and counts", lines);
  }
  return entries.sort((a, b) => a.businessDate.localeCompare(b.businessDate) || a.id.localeCompare(b.id));
}

/**
 * Whether a bill counts as a sale in the books. A takeaway or call order stays "open" after it is paid until staff mark it
 * collected, and many are never marked: so a bill counts once it is completed or fully paid (also when refunded later, which
 * the refund entries reverse). Cancelled bills and bills not yet paid do not count.
 */
export const isSale = (order: Pick<Order, "status" | "paymentState">) =>
  order.status === "completed" || (order.status === "open" && ["paid", "partially_refunded", "refunded", "complimentary"].includes(order.paymentState));

export const journalAsEntry = (journal: JournalEntry): LedgerEntry => ({
  id: journal.id, number: journal.number, businessDate: journal.businessDate, memo: journal.memo, reference: journal.reference,
  lines: journal.lines, origin: "journal", kind: journal.source, reversed: Boolean(journal.reversedBy)
});

// ---------------------------------------------------------------- reports

export type AccountBalance = { account: LedgerAccount; debitMinor: number; creditMinor: number; /** Debit minus credit. */ balanceMinor: number };
/** Debit-side accounts show a positive balance as normal; credit-side ones (liabilities, equity, income) are flipped for display. */
export const naturalSign = (type: AccountType) => type === "asset" || type === "expense" ? 1 : -1;

export function balances(entries: readonly LedgerEntry[], accounts: readonly LedgerAccount[], from: BusinessDate | null, to: BusinessDate): AccountBalance[] {
  const totals = new Map<string, { debitMinor: number; creditMinor: number }>();
  for (const entry of entries) {
    if (entry.businessDate > to || (from && entry.businessDate < from)) continue;
    for (const line of entry.lines) {
      const total = totals.get(line.accountId) ?? { debitMinor: 0, creditMinor: 0 };
      total.debitMinor += line.debitMinor; total.creditMinor += line.creditMinor;
      totals.set(line.accountId, total);
    }
  }
  const known = new Set(accounts.map((account) => account.id));
  const rows = accounts.map((account) => { const total = totals.get(account.id) ?? { debitMinor: 0, creditMinor: 0 }; return { account, ...total, balanceMinor: total.debitMinor - total.creditMinor }; });
  // Postings to an account that is not in the chart (a missing system key) still show, so the trial balance stays balanced.
  for (const [id, total] of totals) if (!known.has(id)) rows.push({ account: { id, code: "????", name: `Unmapped: ${id.replace(/^missing:/, "")}`, type: "asset", active: true, createdAt: "", updatedAt: "", schemaVersion: 1 }, ...total, balanceMinor: total.debitMinor - total.creditMinor });
  return rows.sort((a, b) => a.account.code.localeCompare(b.account.code));
}

export type TrialBalance = { rows: AccountBalance[]; debitMinor: number; creditMinor: number };
export function trialBalance(entries: readonly LedgerEntry[], accounts: readonly LedgerAccount[], to: BusinessDate): TrialBalance {
  const rows = balances(entries, accounts, null, to).filter((row) => row.debitMinor !== 0 || row.creditMinor !== 0);
  return {
    rows, debitMinor: rows.reduce((sum, row) => sum + Math.max(row.balanceMinor, 0), 0), creditMinor: rows.reduce((sum, row) => sum + Math.max(-row.balanceMinor, 0), 0)
  };
}

export type ProfitAndLoss = {
  income: AccountBalance[]; costOfSales: AccountBalance[]; expenses: AccountBalance[];
  incomeMinor: number; costOfSalesMinor: number; grossProfitMinor: number; expensesMinor: number; netProfitMinor: number;
};
/** Income and expense for a period. Amounts are shown in their natural direction (income positive, expenses positive). */
export function profitAndLoss(entries: readonly LedgerEntry[], accounts: readonly LedgerAccount[], from: BusinessDate, to: BusinessDate, map: AccountMap): ProfitAndLoss {
  const rows = balances(entries, accounts, from, to).filter((row) => row.balanceMinor !== 0);
  const costIds = new Set([map.key("cogs"), map.key("stock_loss")]);
  const income = rows.filter((row) => row.account.type === "income");
  const costOfSales = rows.filter((row) => row.account.type === "expense" && costIds.has(row.account.id));
  const expenses = rows.filter((row) => row.account.type === "expense" && !costIds.has(row.account.id));
  const incomeMinor = -income.reduce((sum, row) => sum + row.balanceMinor, 0);
  const costOfSalesMinor = costOfSales.reduce((sum, row) => sum + row.balanceMinor, 0);
  const expensesMinor = expenses.reduce((sum, row) => sum + row.balanceMinor, 0);
  return { income, costOfSales, expenses, incomeMinor, costOfSalesMinor, grossProfitMinor: incomeMinor - costOfSalesMinor, expensesMinor, netProfitMinor: incomeMinor - costOfSalesMinor - expensesMinor };
}

export type BalanceSheet = {
  assets: AccountBalance[]; liabilities: AccountBalance[]; equity: AccountBalance[];
  assetsMinor: number; liabilitiesMinor: number; equityMinor: number; /** Profit not yet closed into an equity account. */ unclosedProfitMinor: number;
};
/** Position on a date. Profit since the books began (income less expenses not closed by a journal) shows as its own equity line. */
export function balanceSheet(entries: readonly LedgerEntry[], accounts: readonly LedgerAccount[], asOf: BusinessDate): BalanceSheet {
  const rows = balances(entries, accounts, null, asOf).filter((row) => row.balanceMinor !== 0);
  const assets = rows.filter((row) => row.account.type === "asset");
  const liabilities = rows.filter((row) => row.account.type === "liability");
  const equity = rows.filter((row) => row.account.type === "equity");
  const unclosedProfitMinor = -rows.filter((row) => row.account.type === "income" || row.account.type === "expense").reduce((sum, row) => sum + row.balanceMinor, 0);
  const assetsMinor = assets.reduce((sum, row) => sum + row.balanceMinor, 0);
  const liabilitiesMinor = -liabilities.reduce((sum, row) => sum + row.balanceMinor, 0);
  const equityMinor = -equity.reduce((sum, row) => sum + row.balanceMinor, 0) + unclosedProfitMinor;
  return { assets, liabilities, equity, assetsMinor, liabilitiesMinor, equityMinor, unclosedProfitMinor };
}

export type StatementRow = { entry: LedgerEntry; line: LedgerLine; runningMinor: number };
/** Every posting to one account in a period, with the balance carried in from before it and a running balance (debit minus credit). */
export function accountStatement(entries: readonly LedgerEntry[], accountId: string, from: BusinessDate, to: BusinessDate): { openingMinor: number; rows: StatementRow[]; closingMinor: number } {
  let running = 0;
  const rows: StatementRow[] = [];
  const sorted = [...entries].sort((a, b) => a.businessDate.localeCompare(b.businessDate) || a.number.localeCompare(b.number));
  for (const entry of sorted) {
    if (entry.businessDate > to) break;
    for (const line of entry.lines) {
      if (line.accountId !== accountId) continue;
      running += line.debitMinor - line.creditMinor;
      if (entry.businessDate >= from) rows.push({ entry, line, runningMinor: running });
    }
  }
  const openingMinor = rows.length > 0 ? rows[0]!.runningMinor - rows[0]!.line.debitMinor + rows[0]!.line.creditMinor : running;
  return { openingMinor, rows, closingMinor: running };
}

// ---------------------------------------------------------------- bank

/**
 * Card takings reach the bank days later, less the bank's fee: the card takings settled leave Card settlements, what was
 * credited goes to the bank, and the difference is the fee.
 */
export function cardSettlementLines(map: AccountMap, settledMinor: number, creditedMinor: number, bankAccountId: string, feeAccountId: string): LedgerLine[] {
  return new Lines().debit(bankAccountId, creditedMinor, "Card takings credited").debit(feeAccountId, settledMinor - creditedMinor, "Card fees")
    .credit(map.key("card"), settledMinor, "Card takings settled").list();
}

/** A line on a bank account in the books: money in (+) or out (-). Its key stays the same when the books are worked out again. */
export type BankLine = { key: string; entry: LedgerEntry; memo: string; amountMinor: number };
export const bankLineKey = (entryId: string, side: "in" | "out") => `${entryId}|${side}`;
export function bankLines(entries: readonly LedgerEntry[], accountId: string, to: BusinessDate): BankLine[] {
  return entries.filter((entry) => entry.businessDate <= to).sort((a, b) => a.businessDate.localeCompare(b.businessDate) || a.number.localeCompare(b.number))
    .flatMap((entry) => entry.lines.filter((line) => line.accountId === accountId && line.debitMinor !== line.creditMinor).map((line) => {
      const amountMinor = line.debitMinor - line.creditMinor;
      return { key: bankLineKey(entry.id, amountMinor > 0 ? "in" : "out"), entry, memo: line.memo || entry.memo, amountMinor };
    }));
}
/**
 * A bank reconciliation: lines ticked on earlier statements stay cleared (as long as their amount has not changed); the
 * ticked lines' total should equal the statement's balance. Lines in the books not on the statement yet are listed.
 */
export function reconcile(entries: readonly LedgerEntry[], accountId: string, statementDate: BusinessDate, statementBalanceMinor: number, cleared: Readonly<Record<string, number>>) {
  const lines = bankLines(entries, accountId, statementDate).map((line) => ({ ...line, cleared: cleared[line.key] === line.amountMinor }));
  const clearedMinor = lines.filter((line) => line.cleared).reduce((sum, line) => sum + line.amountMinor, 0);
  const bookMinor = lines.reduce((sum, line) => sum + line.amountMinor, 0);
  return { lines, clearedMinor, bookMinor, unclearedMinor: bookMinor - clearedMinor, differenceMinor: statementBalanceMinor - clearedMinor };
}

// ---------------------------------------------------------------- daily sales summary

export type DailySales = {
  businessDate: BusinessDate; bills: number; grossMinor: number; complimentaryMinor: number; discountMinor: number; netMinor: number;
  serviceChargeMinor: number; taxMinor: number; totalMinor: number; costMinor: number;
  received: Record<PaymentMethod, number>; refundedMinor: number; expensesMinor: number;
};
const noMethods = (): Record<PaymentMethod, number> => ({ cash: 0, card: 0, bank_transfer: 0 });
export function dailySales(records: Pick<PosRecords, "orders" | "payments" | "refunds" | "expenses">, from: BusinessDate, to: BusinessDate): DailySales[] {
  const days = new Map<string, DailySales>();
  const day = (businessDate: string) => {
    let row = days.get(businessDate);
    if (!row) days.set(businessDate, row = { businessDate, bills: 0, grossMinor: 0, complimentaryMinor: 0, discountMinor: 0, netMinor: 0, serviceChargeMinor: 0, taxMinor: 0, totalMinor: 0, costMinor: 0, received: noMethods(), refundedMinor: 0, expensesMinor: 0 });
    return row;
  };
  const inRange = (date: string) => date >= from && date <= to;
  for (const order of records.orders) {
    if (!isSale(order) || !inRange(order.businessDate)) continue;
    const row = day(order.businessDate), t = order.totals;
    row.bills += 1; row.grossMinor += t.grossMinor; row.complimentaryMinor += t.complimentaryMinor; row.discountMinor += t.discountMinor; row.netMinor += t.subtotalMinor;
    row.serviceChargeMinor += t.serviceChargeMinor; row.taxMinor += t.taxMinor; row.totalMinor += t.totalMinor; row.costMinor += t.costMinor;
  }
  for (const payment of records.payments) if (inRange(payment.businessDate)) day(payment.businessDate).received[payment.method] += payment.amountMinor;
  for (const refund of records.refunds) if (inRange(refund.businessDate)) day(refund.businessDate).refundedMinor += refund.amountMinor;
  for (const expense of records.expenses) if (!expense.voided && inRange(expense.businessDate)) day(expense.businessDate).expensesMinor += expense.amountMinor;
  return [...days.values()].sort((a, b) => a.businessDate.localeCompare(b.businessDate));
}

// ---------------------------------------------------------------- tax

export type VatReturn = {
  taxableSalesMinor: number; serviceChargeMinor: number; outputVatMinor: number; refundedVatMinor: number; netOutputVatMinor: number;
  inputVatMinor: number; payableMinor: number; turnoverMinor: number; ssclBaseMinor: number; ssclMinor: number;
};
/**
 * VAT and SSCL for a period from the POS bills (the tax the POS charged), less refunds, and the VAT input the accountant
 * posted to the VAT input account. Rates and the SSCL base come from the accounting settings; these are working figures for
 * the return, to be checked against the Inland Revenue's current rules.
 */
export function vatReturn(records: Pick<PosRecords, "orders" | "refunds">, entries: readonly LedgerEntry[], map: AccountMap, settings: Pick<AccountingSettings, "ssclBps" | "ssclLiableBps">, from: BusinessDate, to: BusinessDate): VatReturn {
  const inRange = (date: string) => date >= from && date <= to;
  const ordersById = new Map(records.orders.map((order) => [order.id, order]));
  let taxableSalesMinor = 0, serviceChargeMinor = 0, outputVatMinor = 0, refundedVatMinor = 0, refundedNetMinor = 0, refundedServiceMinor = 0;
  for (const order of records.orders) {
    if (!isSale(order) || !inRange(order.businessDate)) continue;
    taxableSalesMinor += order.totals.subtotalMinor; serviceChargeMinor += order.totals.serviceChargeMinor; outputVatMinor += order.totals.taxMinor;
  }
  for (const refund of records.refunds) {
    if (!inRange(refund.businessDate)) continue;
    const split = splitRefund(refund, ordersById.get(refund.orderId));
    refundedVatMinor += split.taxMinor; refundedNetMinor += split.netMinor; refundedServiceMinor += split.serviceMinor;
  }
  const vatInput = map.key("vat_input");
  let inputVatMinor = 0;
  for (const entry of entries) {
    if (entry.origin !== "journal" || !inRange(entry.businessDate)) continue;
    for (const line of entry.lines) if (line.accountId === vatInput) inputVatMinor += line.debitMinor - line.creditMinor;
  }
  const netOutputVatMinor = outputVatMinor - refundedVatMinor;
  const turnoverMinor = taxableSalesMinor - refundedNetMinor + serviceChargeMinor - refundedServiceMinor;
  const ssclBaseMinor = percentageOf(Math.max(turnoverMinor, 0), settings.ssclLiableBps);
  return {
    taxableSalesMinor: taxableSalesMinor - refundedNetMinor, serviceChargeMinor: serviceChargeMinor - refundedServiceMinor, outputVatMinor, refundedVatMinor, netOutputVatMinor,
    inputVatMinor, payableMinor: netOutputVatMinor - inputVatMinor, turnoverMinor, ssclBaseMinor, ssclMinor: percentageOf(ssclBaseMinor, settings.ssclBps)
  };
}

// ---------------------------------------------------------------- payroll

/** Monthly APIT on taxable pay, by the settings' relief and bands. Rounded to the rupee, as the tables are. */
export function apitFor(taxableMinor: number, settings: Pick<AccountingSettings, "apitReliefMinor" | "apitSlabs">): number {
  let rest = Math.max(0, taxableMinor - settings.apitReliefMinor), tax = 0;
  for (const slab of settings.apitSlabs) {
    if (rest <= 0) break;
    const part = slab.widthMinor === null ? rest : Math.min(rest, slab.widthMinor);
    tax += part * slab.bps / 10_000;
    rest -= part;
  }
  return Math.round(tax / 100) * 100;
}

export type PayrollInput = {
  employeeId: string; code: string; name: string; nic: string; epfNo: string; designation: string; bankAccount: string;
  basicMinor: number; allowances: EmployeeAllowance[]; overtimeMinor: number; serviceChargeMinor: number; otherEarningsMinor: number;
  advanceMinor: number; otherDeductionMinor: number; /** A figure from the APIT tables typed in by the accountant instead of the worked-out one. */ apitOverrideMinor?: number | null | undefined;
  /** Days absent without pay (a half day is 0.5): a day's pay (salary ÷ noPayDivisor) each is taken off. */ noPayDays?: number | undefined;
};
/** A day's pay and an hour's overtime pay for a monthly salary, by the accounting settings (÷30, ÷240 at 150% unless changed). */
export function dayRate(monthlyMinor: number, settings: Pick<AccountingSettings, "noPayDivisor">): number { return monthlyMinor / (settings.noPayDivisor || 30); }
export function overtimePay(monthlyMinor: number, otMinutes: number, settings: Pick<AccountingSettings, "otDivisor" | "otRateBps">): number {
  return Math.round(monthlyMinor / (settings.otDivisor || 240) * (otMinutes / 60) * (settings.otRateBps ?? 15_000) / 10_000);
}
export const DEFAULT_NORMAL_DAY_MINUTES = 9 * 60;
export const DEFAULT_LATE_GRACE_MINUTES = 10;
/**
 * How late someone arrived: minutes after their shift start (Colombo time), or 0 when they came within the grace minutes,
 * have no shift start, or no arrival time.
 */
export function lateMinutes(clockIn: string | null | undefined, shiftStart: string | null | undefined, graceMinutes = DEFAULT_LATE_GRACE_MINUTES): number {
  if (!clockIn || !shiftStart) return 0;
  const [hours, minutes] = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Colombo", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(clockIn)).split(":").map(Number);
  const [startHours, startMinutes] = shiftStart.split(":").map(Number);
  const late = hours! * 60 + minutes! - (startHours! * 60 + startMinutes!);
  return late > graceMinutes ? late : 0;
}

/** Overtime for one day: the time between arrival and leaving beyond a normal day (whole minutes). */
export function overtimeMinutes(clockIn: string | null, clockOut: string | null, normalDayMinutes = DEFAULT_NORMAL_DAY_MINUTES): number {
  if (!clockIn || !clockOut) return 0;
  return Math.max(0, Math.round((Date.parse(clockOut) - Date.parse(clockIn)) / 60_000) - normalDayMinutes);
}

/**
 * Reads a time as people type it from the attendance book: "8", "8.20", "820", "0820", "8:20", "20.20", "8.20pm", "8 pm".
 * Without am/pm it is a 24-hour time. Returns "HH:MM", or null when it cannot be read.
 */
export function parseClockTime(text: string): string | null {
  const match = /^(\d{1,2})(?:[:.\s]?(\d{2}))?\s*(a|am|p|pm)?$/i.exec(text.trim().replace(/\.$/, ""));
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] ?? 0), half = match[3]?.toLowerCase();
  if (minute > 59) return null;
  if (half) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (half.startsWith("p") ? 12 : 0);
  }
  if (hour > 23) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}
/** "20:30" → "8:30 PM", to show beside a time box. */
export const clockTimeLabel = (time: string) => { const [hour, minute] = time.split(":").map(Number) as [number, number]; return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`; };

/** Days off without pay in a month's attendance: absent counts 1, a half day 0.5. Leave, holidays and days off are paid. */
export function noPayDaysOf(records: readonly { status: string }[]): number {
  return records.reduce((sum, record) => sum + (record.status === "absent" ? 1 : record.status === "half" ? 0.5 : 0), 0);
}
/**
 * One employee's month: EPF (employee 8%, employer 12%) and ETF (3%) on basic pay and EPF-liable allowances, APIT on all
 * earnings, then the net pay after deductions. Rates come from the accounting settings.
 */
export function payrollLine(input: PayrollInput, settings: Pick<AccountingSettings, "epfEmployeeBps" | "epfEmployerBps" | "etfBps" | "apitReliefMinor" | "apitSlabs" | "noPayDivisor">): PayrollLine {
  const allowancesMinor = input.allowances.reduce((sum, allowance) => sum + allowance.amountMinor, 0);
  const liableMinor = input.basicMinor + input.allowances.filter((allowance) => allowance.epfLiable).reduce((sum, allowance) => sum + allowance.amountMinor, 0);
  // No-pay days come off the salary (and so off the EPF earnings, in the same proportion).
  const noPayDays = Math.max(0, input.noPayDays ?? 0);
  const noPayMinor = Math.min(input.basicMinor + allowancesMinor, Math.round(dayRate(input.basicMinor + allowancesMinor, settings) * noPayDays));
  const grossMinor = input.basicMinor + allowancesMinor - noPayMinor + input.overtimeMinor + input.serviceChargeMinor + input.otherEarningsMinor;
  const epfBaseMinor = Math.max(0, liableMinor - Math.round(dayRate(liableMinor, settings) * noPayDays));
  const epfEmployeeMinor = percentageOf(epfBaseMinor, settings.epfEmployeeBps);
  const apitMinor = input.apitOverrideMinor ?? apitFor(grossMinor, settings);
  const { apitOverrideMinor: _ignored, noPayDays: _days, ...fields } = input;
  return {
    ...fields, ...(noPayDays > 0 ? { noPayDays, noPayMinor } : {}), grossMinor, epfBaseMinor, epfEmployeeMinor, epfEmployerMinor: percentageOf(epfBaseMinor, settings.epfEmployerBps), etfMinor: percentageOf(epfBaseMinor, settings.etfBps),
    apitMinor, netMinor: grossMinor - epfEmployeeMinor - apitMinor - input.advanceMinor - input.otherDeductionMinor
  };
}
/**
 * What a person still owes in salary advances at the end of a month: every advance paid from the POS up to then, less
 * what earlier payrolls (not voided) already took off. The month's payroll suggests taking off all of it.
 */
export function outstandingAdvance(employeeId: string, month: string, expenses: readonly Pick<Expense, "category" | "employeeId" | "amountMinor" | "voided" | "businessDate">[], runs: readonly Pick<PayrollRun, "month" | "status" | "lines">[]): number {
  const given = new Map<string, number>(), recovered = new Map<string, number>();
  for (const expense of expenses) if (expense.category === SALARY_ADVANCE && expense.employeeId === employeeId && !expense.voided) given.set(expense.businessDate.slice(0, 7), (given.get(expense.businessDate.slice(0, 7)) ?? 0) + expense.amountMinor);
  for (const run of runs) if (run.status !== "voided" && run.month < month) for (const line of run.lines) if (line.employeeId === employeeId) recovered.set(run.month, (recovered.get(run.month) ?? 0) + line.advanceMinor);
  // Month by month, so an advance taken off a salary but paid outside the POS never lowers what the POS advances leave owed.
  let owed = 0;
  for (const key of [...new Set([...given.keys(), ...recovered.keys()])].filter((key) => key <= month).sort()) owed = Math.max(0, owed + (given.get(key) ?? 0) - (recovered.get(key) ?? 0));
  return owed;
}
export function payrollTotals(lines: readonly PayrollLine[]): PayrollTotals {
  const total = (pick: (line: PayrollLine) => number) => lines.reduce((sum, line) => sum + pick(line), 0);
  return {
    grossMinor: total((line) => line.grossMinor), epfBaseMinor: total((line) => line.epfBaseMinor), epfEmployeeMinor: total((line) => line.epfEmployeeMinor),
    epfEmployerMinor: total((line) => line.epfEmployerMinor), etfMinor: total((line) => line.etfMinor), apitMinor: total((line) => line.apitMinor),
    advanceMinor: total((line) => line.advanceMinor), otherDeductionMinor: total((line) => line.otherDeductionMinor), netMinor: total((line) => line.netMinor),
    serviceChargeMinor: total((line) => line.serviceChargeMinor)
  };
}
/**
 * What each posted payroll leaves to pay to the funds and the IRD, and by when: EPF (8% + 12%) and ETF (3%) by the end of
 * the next month, APIT by the 15th of the next month. A journal whose reference is "EPF 2026-10" (not reversed) pays it.
 */
export type StatutoryKind = "EPF" | "ETF" | "APIT";
export type StatutoryDue = { kind: StatutoryKind; month: string; amountMinor: number; dueDate: BusinessDate; paidJournalId: string | null };
export const statutoryReference = (kind: StatutoryKind, month: string) => `${kind} ${month}`;
export function statutoryDues(runs: readonly Pick<PayrollRun, "month" | "status" | "totals">[], journals: readonly Pick<JournalEntry, "id" | "reference" | "reversalOf" | "reversedBy">[]): StatutoryDue[] {
  const paid = new Map(journals.filter((journal) => !journal.reversalOf && !journal.reversedBy).map((journal) => [journal.reference, journal.id]));
  return runs.filter((run) => run.status !== "voided").sort((a, b) => a.month.localeCompare(b.month)).flatMap((run) => {
    const next = addDays(`${run.month}-28`, 7).slice(0, 7);
    const monthEnd = addDays(`${addDays(`${next}-28`, 7).slice(0, 7)}-01`, -1);
    return ([["EPF", run.totals.epfEmployeeMinor + run.totals.epfEmployerMinor, monthEnd], ["ETF", run.totals.etfMinor, monthEnd], ["APIT", run.totals.apitMinor, `${next}-15`]] as const)
      .filter(([, amountMinor]) => amountMinor > 0)
      .map(([kind, amountMinor, dueDate]) => ({ kind, month: run.month, amountMinor, dueDate, paidJournalId: paid.get(statutoryReference(kind, run.month)) ?? null }));
  });
}
/** Paying one: the amount owed to the fund or the IRD, out of the bank (or cash). */
export function statutoryPaymentLines(due: StatutoryDue, map: AccountMap, method: PaymentMethod): LedgerLine[] {
  const payable = map.key(due.kind === "EPF" ? "epf_payable" : due.kind === "ETF" ? "etf_payable" : "apit_payable");
  return new Lines().debit(payable, due.amountMinor, `${due.kind} for ${due.month}`).credit(map.method(method), due.amountMinor, `${due.kind} paid`).list();
}

/** The payroll journal: salary cost and employer contributions against what is now owed to staff, the EPF/ETF funds and the IRD. */
export function payrollJournalLines(totals: PayrollTotals, map: AccountMap): LedgerLine[] {
  return new Lines()
    .debit(map.key("salaries"), totals.grossMinor - totals.serviceChargeMinor, "Salaries & wages")
    .debit(map.key("service_charge"), totals.serviceChargeMinor, "Service charge paid to staff")
    .debit(map.key("epf_employer"), totals.epfEmployerMinor, "EPF employer")
    .debit(map.key("etf_expense"), totals.etfMinor, "ETF")
    .credit(map.key("epf_payable"), totals.epfEmployeeMinor + totals.epfEmployerMinor, "EPF 8% + 12%")
    .credit(map.key("etf_payable"), totals.etfMinor, "ETF 3%")
    .credit(map.key("apit_payable"), totals.apitMinor, "APIT")
    .credit(map.key("salary_advances"), totals.advanceMinor, "Advances recovered")
    .credit(map.key("staff_deductions"), totals.otherDeductionMinor, "Other deductions")
    .credit(map.key("salaries_payable"), totals.netMinor, "Net pay")
    .list();
}

/** "2026-10" -> "2026-10-31". */
export function monthEnd(month: string): BusinessDate {
  const [year, number] = month.split("-").map(Number) as [number, number];
  const next = number === 12 ? `${year + 1}-01-01` : `${year}-${String(number + 1).padStart(2, "0")}-01`;
  return addDays(next, -1);
}

// ---------------------------------------------------------------- supplier statement

export type StatementLine = {
  id: string; businessDate: BusinessDate; at: string; kind: "purchase" | "bill" | "payment" | "advance";
  reference: string; detail: string; /** Added to what is owed (a bill or a purchase on credit). */ owedMinor: number; /** Paid to the supplier. */ paidMinor: number; balanceMinor: number;
};
/**
 * Everything with one supplier, oldest first, with what was owed after each line. The last balance is the supplier's
 * current balance as the POS keeps it; lines older than the records on hand show as the balance brought forward.
 * Purchases paid at once are listed for reference but change nothing owed.
 */
export function supplierStatement(supplier: { id: string; balanceMinor?: number }, records: { purchases: readonly Purchase[]; supplierPayments: readonly SupplierPayment[]; supplierBills?: readonly SupplierBill[] }) {
  const lines: Omit<StatementLine, "balanceMinor">[] = [
    ...records.purchases.filter((purchase) => purchase.supplierId === supplier.id).map((purchase) => ({
      id: purchase.id, businessDate: purchase.businessDate, at: purchase.createdAt, kind: "purchase" as const, reference: purchase.invoiceNo,
      detail: `${purchase.onCredit ? "Stock on credit" : "Stock paid at once"}: ${purchase.lines.map((line) => line.name).join(", ")}`, owedMinor: purchase.onCredit ? purchase.totalMinor + (purchase.vatMinor ?? 0) : 0, paidMinor: 0
    })),
    ...(records.supplierBills ?? []).filter((bill) => bill.supplierId === supplier.id && !bill.voided).map((bill) => ({
      id: bill.id, businessDate: bill.businessDate, at: bill.createdAt, kind: "bill" as const, reference: [bill.number, bill.invoiceNo].filter(Boolean).join(" / "),
      detail: `${bill.kind === "stock" ? "Stock" : bill.category}${bill.description ? ` - ${bill.description}` : ""}`, owedMinor: bill.amountMinor, paidMinor: 0
    })),
    ...records.supplierPayments.filter((payment) => payment.supplierId === supplier.id).map((payment) => ({
      id: payment.id, businessDate: payment.businessDate, at: payment.createdAt, kind: payment.advance ? "advance" as const : "payment" as const, reference: payment.method.replace("_", " "),
      detail: payment.note || (payment.advance ? "Advance payment" : "Payment"), owedMinor: 0, paidMinor: payment.amountMinor
    }))
  ].sort((a, b) => a.businessDate.localeCompare(b.businessDate) || a.at.localeCompare(b.at));
  const closingMinor = supplier.balanceMinor ?? 0;
  const openingMinor = closingMinor - lines.reduce((sum, line) => sum + line.owedMinor - line.paidMinor, 0);
  let running = openingMinor;
  return { openingMinor, closingMinor, lines: lines.map((line): StatementLine => ({ ...line, balanceMinor: running += line.owedMinor - line.paidMinor })),
    billedMinor: lines.reduce((sum, line) => sum + line.owedMinor, 0), paidMinor: lines.reduce((sum, line) => sum + line.paidMinor, 0),
    advancesMinor: lines.filter((line) => line.kind === "advance").reduce((sum, line) => sum + line.paidMinor, 0) };
}

/**
 * Supplier bills not yet paid, and when they fall due. Payments are made against the supplier's balance, not a bill, so
 * what is still owed is taken to be the newest bills and credit purchases (the oldest are paid first). Bills without a
 * due date are left out. `soon` lists those due within that many days of `today`.
 */
export type BillDue = { bill: SupplierBill; unpaidMinor: number; overdue: boolean };
export function billsDue(suppliers: readonly { id: string; balanceMinor?: number }[], records: { purchases: readonly Purchase[]; supplierBills: readonly SupplierBill[] }, today: BusinessDate, soon = 7): BillDue[] {
  const until = addDays(today, soon);
  return suppliers.flatMap((supplier) => {
    let left = Math.max(0, supplier.balanceMinor ?? 0);
    const owed = [
      ...records.supplierBills.filter((bill) => bill.supplierId === supplier.id && !bill.voided).map((bill) => ({ bill, at: bill.createdAt, number: bill.number, date: bill.businessDate, amountMinor: bill.amountMinor })),
      ...records.purchases.filter((purchase) => purchase.supplierId === supplier.id && purchase.onCredit).map((purchase) => ({ bill: null, at: purchase.createdAt, number: "", date: purchase.businessDate, amountMinor: purchase.totalMinor + (purchase.vatMinor ?? 0) }))
    ].sort((a, b) => b.date.localeCompare(a.date) || b.at.localeCompare(a.at) || b.number.localeCompare(a.number));
    const due: BillDue[] = [];
    for (const item of owed) {
      if (left <= 0) break;
      const unpaidMinor = Math.min(left, item.amountMinor);
      left -= unpaidMinor;
      if (item.bill?.dueDate && item.bill.dueDate <= until) due.push({ bill: item.bill, unpaidMinor, overdue: item.bill.dueDate < today });
    }
    return due;
  }).sort((a, b) => a.bill.dueDate!.localeCompare(b.bill.dueDate!));
}

// ---------------------------------------------------------------- month-end stock

/** What the stock on hand is worth now in the POS: each item's quantity (never below zero) at its average cost. */
export function stockValueMinor(items: readonly Pick<InventoryItem, "stockMilli" | "avgCostMinor" | "active" | "removed">[]): number {
  return items.filter((item) => item.active && !item.removed).reduce((sum, item) => sum + Math.round(Math.max(0, item.stockMilli) * item.avgCostMinor / 1000), 0);
}

/**
 * The journal that brings the stock account in the books to what the stock is worth. With periodic costing the change goes
 * to cost of sales (more stock left = less was used up); with perpetual costing the difference is waste or count loss.
 * Returns no lines when the books already agree.
 */
export function stockAdjustmentLines(countedMinor: number, bookMinor: number, map: AccountMap, costing: AccountingSettings["costing"] = "periodic"): LedgerLine[] {
  const difference = countedMinor - bookMinor;
  const counter = map.key(costing === "perpetual" ? "stock_loss" : "cogs");
  if (difference === 0) return [];
  return difference > 0
    ? [{ accountId: map.key("inventory"), debitMinor: difference, creditMinor: 0, memo: "Stock up to the count" }, { accountId: counter, debitMinor: 0, creditMinor: difference, memo: "" }]
    : [{ accountId: counter, debitMinor: -difference, creditMinor: 0, memo: "" }, { accountId: map.key("inventory"), debitMinor: 0, creditMinor: -difference, memo: "Stock down to the count" }];
}
